import React from 'react';
import { Box, Checkbox, FormControlLabel, MenuItem, TextField } from '@mui/material';
import Group from 'components/common/Group';
import { renderField } from 'components/utils';
import { fetchZipRecruiterLocationSuggestions } from './fetchZipRecruiterLocationSuggestions';
import { useExtensionDetection } from 'lib/useExtensionDetection';
import ExtensionLocationAutocomplete from './ExtensionLocationAutocomplete';
import { ZipRecruiterFields } from '../types';
import {
  isValidZipRecruiterCompanyText,
  resolveZipRecruiterEmploymentType,
  zipRecruiterEmploymentTypeOptions,
  zipRecruiterMinCompanyTextLength,
  zipRecruiterPayCurrencyOptions,
  zipRecruiterPayFrequencyOptions,
  zipRecruiterPayTypeOptions,
  zipRecruiterPreferredDistanceOptions,
  zipRecruiterRemoteJobOptions,
  zipRecruiterBenefitOptions,
} from './zipRecruiterConstants';

const companyTextHelper = (value?: string) => {
  const length = value?.trim().length ?? 0;

  if (length === 0) {
    return `Optional on ZipRecruiter. If provided, use at least ${zipRecruiterMinCompanyTextLength} characters.`;
  }

  if (!isValidZipRecruiterCompanyText(value)) {
    return `${length}/${zipRecruiterMinCompanyTextLength} characters — ZipRecruiter requires at least ${zipRecruiterMinCompanyTextLength}.`;
  }

  return undefined;
};

type Props = {
  value: ZipRecruiterFields;
  onChange: (value: ZipRecruiterFields) => void;
  infoAlert?: React.ReactNode;
  wrapInGroup?: boolean;
  hideGroupTitle?: boolean;
};

const flexOptions = {
  label: 3,
  value: 9,
};

const pairedFieldSx = {
  display: 'flex',
  gap: 2,
  flexWrap: 'wrap',
  '& > *': {
    flex: '1 1 140px',
    minWidth: 0,
  },
};

const ZipRecruiterFieldsForm = ({ value, onChange, infoAlert, wrapInGroup = true, hideGroupTitle = false }: Props) => {
  const { installed: extensionInstalled } = useExtensionDetection();

  const updateField = <K extends keyof ZipRecruiterFields>(key: K, next: ZipRecruiterFields[K]) => {
    onChange({ ...value, [key]: next });
  };

  const employmentTypeValue = resolveZipRecruiterEmploymentType(value.employmentType) || '';

  const fields = (
    <>
      {infoAlert}
      {renderField({
        id: 'job_form_ziprecruiter_job_location',
        label: 'Job location',
        flexOptions,
        renderControl: () => (
          <ExtensionLocationAutocomplete
            value={value.jobLocation || ''}
            onChange={next => updateField('jobLocation', next || undefined)}
            extensionInstalled={extensionInstalled}
            platformLabel="ZipRecruiter"
            placeholder="Search ZipRecruiter job location"
            helperText="Select a location from ZipRecruiter suggestions"
            fetchSuggestions={fetchZipRecruiterLocationSuggestions}
          />
        ),
      })}

      {renderField({
        id: 'job_form_ziprecruiter_street_address',
        label: 'Street Address (optional)',
        flexOptions,
        renderControl: () => (
          <TextField
            fullWidth
            size="small"
            value={value.streetAddress || ''}
            onChange={e => updateField('streetAddress', e.target.value)}
          />
        ),
      })}

      {renderField({
        id: 'job_form_ziprecruiter_is_remote_job',
        label: 'Is this a remote job with no on-site work required?',
        flexOptions,
        renderControl: () => (
          <TextField
            select
            fullWidth
            size="small"
            value={value.isRemoteJob === true ? 'yes' : value.isRemoteJob === false ? 'no' : ''}
            onChange={e =>
              updateField('isRemoteJob', e.target.value === 'yes' ? true : e.target.value === 'no' ? false : undefined)
            }
          >
            <MenuItem value="">None</MenuItem>
            {zipRecruiterRemoteJobOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        ),
      })}

      {renderField({
        id: 'job_form_ziprecruiter_employment_type',
        label: 'Employment type',
        flexOptions,
        renderControl: () => (
          <TextField
            select
            fullWidth
            size="small"
            value={employmentTypeValue}
            helperText="Must match ZipRecruiter options exactly"
            onChange={e =>
              updateField(
                'employmentType',
                e.target.value ? resolveZipRecruiterEmploymentType(e.target.value) : undefined,
              )
            }
          >
            <MenuItem value="">None</MenuItem>
            {zipRecruiterEmploymentTypeOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        ),
      })}

      {renderField({
        id: 'job_form_ziprecruiter_relocate',
        label: 'Willing to relocate',
        flexOptions,
        renderControl: () => (
          <FormControlLabel
            control={
              <Checkbox
                checked={value.includeWillingToRelocate ?? false}
                onChange={e => updateField('includeWillingToRelocate', e.target.checked)}
              />
            }
            label="Include candidates willing to relocate"
          />
        ),
      })}

      {renderField({
        id: 'job_form_ziprecruiter_preferred_distance',
        label: 'Preferred applicant distance (optional)',
        flexOptions,
        renderControl: () => (
          <TextField
            select
            fullWidth
            size="small"
            value={value.preferredDistance ?? ''}
            onChange={e => updateField('preferredDistance', e.target.value ? Number(e.target.value) : undefined)}
          >
            <MenuItem value="">None</MenuItem>
            {zipRecruiterPreferredDistanceOptions.map(distance => (
              <MenuItem key={distance} value={distance}>
                {distance} miles
              </MenuItem>
            ))}
          </TextField>
        ),
      })}

      {renderField({
        id: 'job_form_ziprecruiter_pay_type',
        label: 'Pay type',
        flexOptions,
        renderControl: () => (
          <TextField
            select
            fullWidth
            size="small"
            value={value.payType || 'Pay range'}
            onChange={e => updateField('payType', e.target.value)}
          >
            {zipRecruiterPayTypeOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        ),
      })}

      {renderField({
        id: 'job_form_ziprecruiter_pay_range',
        label: 'Pay range',
        flexOptions,
        renderControl: () => (
          <Box sx={pairedFieldSx}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Minimum"
              value={value.payMinimum ?? ''}
              onChange={e => updateField('payMinimum', e.target.value ? Number(e.target.value) : undefined)}
            />
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Maximum"
              value={value.payMaximum ?? ''}
              onChange={e => updateField('payMaximum', e.target.value ? Number(e.target.value) : undefined)}
            />
          </Box>
        ),
      })}

      {renderField({
        id: 'job_form_ziprecruiter_pay_currency_frequency',
        label: 'Currency / frequency',
        flexOptions,
        renderControl: () => (
          <Box sx={pairedFieldSx}>
            <TextField
              select
              fullWidth
              size="small"
              label="Currency"
              value={value.payCurrency || 'USD'}
              onChange={e => updateField('payCurrency', e.target.value)}
            >
              {zipRecruiterPayCurrencyOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              size="small"
              label="Frequency"
              value={value.payFrequency || 'Annually'}
              onChange={e => updateField('payFrequency', e.target.value)}
            >
              {zipRecruiterPayFrequencyOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        ),
      })}

      {renderField({
        id: 'job_form_ziprecruiter_pay_commission',
        label: 'Commission',
        flexOptions,
        renderControl: () => (
          <FormControlLabel
            control={
              <Checkbox
                checked={value.payCommission ?? false}
                onChange={e => updateField('payCommission', e.target.checked)}
              />
            }
            label="This role includes commission"
          />
        ),
      })}

      {renderField({
        id: 'job_form_ziprecruiter_benefits',
        label: 'Benefits',
        flexOptions,
        renderControl: () => (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {zipRecruiterBenefitOptions.map(option => {
              const selected = value.benefits || [];

              return (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={selected.includes(option.value)}
                      onChange={e => {
                        const checked = e.target.checked;

                        if (option.value === 'none') {
                          updateField('benefits', checked ? ['none'] : []);
                          return;
                        }

                        const withoutNone = selected.filter(item => item !== 'none');

                        if (checked) {
                          updateField('benefits', [...withoutNone, option.value]);
                          return;
                        }

                        updateField(
                          'benefits',
                          withoutNone.filter(item => item !== option.value),
                        );
                      }}
                    />
                  }
                  label={option.label}
                />
              );
            })}
          </Box>
        ),
      })}

      {renderField({
        id: 'job_form_ziprecruiter_hiring_company',
        label: 'Hiring company',
        flexOptions,
        renderControl: () => (
          <TextField
            fullWidth
            size="small"
            helperText="Must match a company name in your ZipRecruiter account"
            value={value.hiringCompanyName || ''}
            onChange={e => updateField('hiringCompanyName', e.target.value)}
          />
        ),
      })}

      {renderField({
        id: 'job_form_ziprecruiter_why_work_here',
        label: 'Why work at this company? (optional)',
        flexOptions,
        renderControl: () => (
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            value={value.hiringCompanyWhyWorkHere || ''}
            helperText={companyTextHelper(value.hiringCompanyWhyWorkHere)}
            error={
              !!value.hiringCompanyWhyWorkHere?.trim() &&
              !isValidZipRecruiterCompanyText(value.hiringCompanyWhyWorkHere)
            }
            onChange={e => updateField('hiringCompanyWhyWorkHere', e.target.value)}
          />
        ),
      })}

      {renderField({
        id: 'job_form_ziprecruiter_company_description',
        label: 'Hiring company description (optional)',
        flexOptions,
        renderControl: () => (
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            value={value.hiringCompanyDescription || ''}
            helperText={companyTextHelper(value.hiringCompanyDescription)}
            error={
              !!value.hiringCompanyDescription?.trim() &&
              !isValidZipRecruiterCompanyText(value.hiringCompanyDescription)
            }
            onChange={e => updateField('hiringCompanyDescription', e.target.value)}
          />
        ),
      })}

      {renderField({
        id: 'job_form_ziprecruiter_resume_not_required',
        label: 'Resume requirement',
        flexOptions,
        renderControl: () => (
          <FormControlLabel
            control={
              <Checkbox
                checked={value.resumeNotRequired ?? false}
                onChange={e => updateField('resumeNotRequired', e.target.checked)}
              />
            }
            label="Resume not required"
          />
        ),
      })}
    </>
  );

  if (!wrapInGroup) {
    return fields;
  }

  return (
    <Group
      title={hideGroupTitle ? undefined : 'Job Autofill for ZipRecruiter (optional)'}
      bg="white"
      id="job_form_ziprecruiter_fields"
      isCollapsible={!hideGroupTitle}
      defaultExpanded={false}
    >
      {fields}
    </Group>
  );
};

export default ZipRecruiterFieldsForm;
